import { useEffect, useMemo, useState } from 'react';
import { useOnRobbieApplied } from '../contexts/RobbieUiContext';
import { AwardSplitPayload } from '../types/robbieProposal';
import {
    AwardAllocation, AwardPlan, QuoteComponentContext, QuoteRecommendation, QuoteRecommendationResult,
} from '../types/quote.types';

type DraftPlans = Record<string, AwardAllocation[]>;

const plain = (allocations: AwardAllocation[]) =>
    allocations.map(a => ({ supplierName: a.supplierName, sharePercent: a.sharePercent }));

/**
 * The buyer's editable award split. Re-seeded whenever fresh server state
 * arrives: a confirmed plan wins, otherwise Robbie's recommendation.
 */
export const useAwardDraft = (
    result: QuoteRecommendationResult | null,
    componentContext: QuoteComponentContext[],
    recommendation: QuoteRecommendation | null,
    awardPlan: AwardPlan | null,
) => {
    const [draftPlans, setDraftPlans] = useState<DraftPlans>({});

    useEffect(() => {
        if (!result) return;
        const seeded: DraftPlans = {};
        componentContext.forEach(component => {
            const fromPlan = awardPlan?.components.find(c => c.componentId === component.componentId)?.allocations;
            const fromAI = recommendation?.components.find(c => c.componentId === component.componentId)?.allocations;
            seeded[component.componentId] = plain(fromPlan || fromAI || []);
        });
        setDraftPlans(seeded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.generatedAt, result?.awardPlan?.decidedAt, componentContext.length]);

    // A Robbie split the buyer applied moves the sliders; Confirm award is
    // still theirs. Held until the plan has loaded, then laid over the seed.
    const [robbieSplit, setRobbieSplit] = useState<AwardSplitPayload | null>(null);
    useOnRobbieApplied('award_split', ({ components }) => setRobbieSplit({ components }));
    useEffect(() => {
        if (!robbieSplit || !result) return;
        setDraftPlans(prev => {
            const next = { ...prev };
            robbieSplit.components.forEach(c => { next[c.componentId] = plain(c.allocations); });
            return next;
        });
        setRobbieSplit(null);
    }, [robbieSplit, result]);

    const setShare = (componentId: string, supplierName: string, newShare: number) => {
        setDraftPlans(prev => {
            const current = prev[componentId] || [];
            const without = current.filter(a => a.supplierName !== supplierName);
            return {
                ...prev,
                [componentId]: newShare > 0 || current.some(a => a.supplierName === supplierName)
                    ? [...without, { supplierName, sharePercent: newShare }].filter(a => a.sharePercent > 0 || newShare === 0)
                    : current,
            };
        });
    };

    const resetToAI = (componentId: string) => {
        const fromAI = recommendation?.components.find(c => c.componentId === componentId)?.allocations || [];
        setDraftPlans(prev => ({ ...prev, [componentId]: plain(fromAI) }));
    };

    const awardable = useMemo(() => componentContext.filter(c => c.candidates.length > 0), [componentContext]);

    const allComplete = useMemo(() => awardable.every(c =>
        (draftPlans[c.componentId] || []).reduce((sum, a) => sum + a.sharePercent, 0) === 100
    ), [awardable, draftPlans]);

    /** Awarded supplier names in confirmed-plan order — one RFQ document each. */
    const awardedSuppliers = useMemo(() => {
        if (!awardPlan) return [];
        const names = awardPlan.components.flatMap(component =>
            component.allocations.filter(a => a.sharePercent > 0).map(a => a.supplierName));
        return Array.from(new Set(names));
    }, [awardPlan]);

    const draftMatchesConfirmed = useMemo(() => {
        if (!awardPlan) return false;
        return componentContext.every(component => {
            const confirmed = awardPlan.components.find(c => c.componentId === component.componentId)?.allocations || [];
            const draft = (draftPlans[component.componentId] || []).filter(a => a.sharePercent > 0);
            if (confirmed.length !== draft.length) return false;
            return confirmed.every(a => draft.find(d => d.supplierName === a.supplierName)?.sharePercent === a.sharePercent);
        });
    }, [awardPlan, componentContext, draftPlans]);

    /** The plan body the accept endpoint takes. */
    const planComponents = () => awardable.map(c => ({
        componentId: c.componentId,
        allocations: plain((draftPlans[c.componentId] || []).filter(a => a.sharePercent > 0)),
    }));

    return {
        draftPlans, setShare, resetToAI, awardable, allComplete, awardedSuppliers, draftMatchesConfirmed, planComponents,
    };
};

export default useAwardDraft;
